import { Injectable } from '@angular/core';
import { ALL_CUSTOM_ELEMENT_TAGS } from '../../../custom-element-tags.generated';

export interface ModalRegistryEntry {
  ceTagName: string;
  dialogWidth?: string;
  dialogHeight?: string;
  dialogPanelClass?: string;
  templateMode?: string;
}

/**
 * Maps grid column `call_back` values and/or `/getScreenModal` responses
 * to Custom Element tag names so the CeHostDialogComponent knows which
 * element to render inside the MatDialog.
 *
 * Entries are added lazily — start with the screens you are migrating and
 * grow the registry as more pencil-click modals come online.
 */
@Injectable({
  providedIn: 'root',
})
export class ComponentRegistryService {

  private registry = new Map<string, ModalRegistryEntry>();

  constructor() {
    this.seed();
  }

  private seed(): void {
    // ── partnership ──────────────────────────────────────────────
    this.add('F1065-PAGE-1',                       'gtw-f1065-page-one-ce');
    this.add('F1065-SCH-K2',           'gtw-f1065-sch-k-ce');
    this.add('F1065-SCH-K',          'gtw-f1065-sch-k1-ce');
    this.add('F1065-SCH-B',                        'gtw-f1065-sch-b-ce');
    this.add('F1065-SCH-B2',                       'gtw-f1065-sch-b2-ce');
    this.add('F1065-SCH-C',                        'gtw-f1065-sch-c-ce');
    this.add('F1065-SCH-M3',                       'gtw-f1065-sch-m3-ce');
    this.add('F1065-M2',                           'gtw-form-1065-m2-ce');
    this.add('F8865-SCH-M2-K1',                    'gtw-f8865-sch-m2-k1-sourcing');
    this.add('partner_setup',                      'gtw-partnership-setup-ce');
    this.add('edit_pship_code_mast',               'gtw-pship-code-mast-ce');
    this.add('manage_partner',                     'gtw-partner-maintenance-ce');
    this.add('partner_allocation_new',             'gtw-partner-alloc-new-ce');
    this.add('partner_allocation',                 'gtw-partner-alloc-ce');
    this.add('openPartnershipAmended',             'gtw-batch-open-amended-ce');
    this.add('F1065SchdM2Modal',                   'gtw-form-1065-m2-ce');
    this.add('extEntityMaintenanceModal',          'gtw-ext-entity-maintenance-ce');
    this.add('markPshipAsReviewed',                'gtw-mark-as-reviewed-ce');
    this.add('dfAssignEntity',                     'gtw-df-assign-entity-ce');
    this.add('deleteProjectData',                  'gtw-project-delete-modal-ce');
    this.add('deleteProjectStepData',              'gtw-project-delete-modal-ce');
    this.add('runbatchAllocate',                   'gtw-batch-allocate-ce', { dialogWidth: '420px' });
    this.add('apprConfig',                         'gtw-appr-config-ce');
    this.add('apprEditConfig',                     'gtw-appr-config-ce');
    this.add('createSourcingOverride',             'gtw-default-sourcing-ce');
    this.add('editSplAlloc',                       'gtw-pship-spl-alloc');
    this.add('pshipSplAllocModal',                 'gtw-pship-spl-alloc');
    this.add('editSourcingAmounts',                'gtw-sourcing-amounts');
    this.add('pshipSourcingAmtModal',              'gtw-sourcing-amounts');
    this.add('editF8865SchM2K1Sourcing',           'gtw-f8865-sch-m2-k1-sourcing');
    this.add('Form8865SchM2K1Sourcing',            'gtw-f8865-sch-m2-k1-sourcing');
    this.add('editBatchAllocate',                  'gtw-batch-allocate-ce', { dialogWidth: '420px' });
    this.add('editExtEntity',                      'gtw-ext-entity-maintenance-ce');
    this.add('editPartnershipDashboard',           'gtw-partnership-dashboard-ce');
    this.add('editDfAssignEntity',                 'gtw-df-assign-entity-ce');
    this.add('editPshipCodeMast',                  'gtw-pship-code-mast-ce');
    this.add('editPartnershipSetup',               'gtw-partnership-setup-ce');
    this.add('editMarkAsReviewed',                 'gtw-mark-as-reviewed-ce');
    this.add('editProjectDelete',                  'gtw-project-delete-modal-ce');
    this.add('editBatchOpenAmended',               'gtw-batch-open-amended-ce');
    this.add('manage-partner',                     'gtw-partner-maintenance-ce');
    this.add('managePartner',                      'gtw-partner-maintenance-ce');

    // ── ftc ──────────────────────────────────────────────────────
    //this.add('F1065-SCH-K2',                       'gtw-f1065-k2-data-entry-ce');
    this.add('forms1118',                          'gtw-form-1118-ftc-edit-ce');
    this.add('form1118',                           'gtw-form-1118-ftc-edit-ce');
    this.add('form1118Config',                     'gtw-form-1118-config-ce');
    this.add('forms1118Config',                    'gtw-form-1118-config-ce');
    this.add('attachSchedule1118',                 'gtw-f1118-attach-schd-ce');
    this.add('attachSchedule1118HPII',             'gtw-f1118-hp-ii-attach-schd-ce');
    this.add('F1118-SCH-C',                        'gtw-f1118-schd-c-ce');
    this.add('addNewCustomOverride',               'gtw-f1118-schd-c-ce');
    this.add('addNewCustomOverrideSchdDPI',        'gtw-f1118-schd-c-ce');
    this.add('addNewCustomOverrideSchdDPII',       'gtw-f1118-schd-c-ce');
    this.add('addNewCustomOverrideSchdEPI',        'gtw-f1118-schd-c-ce');
    this.add('addNewCustomOverrideSchdEPII',       'gtw-f1118-schd-c-ce');
    this.add('F1118-SCH-J',                        'gtw-form-1118-schd-j-ce');
    this.add('F1118-SCH-K',                        'gtw-form-1118-schd-k-ce');
    this.add('ftcCommentsGeneric',                 'gtw-ftc-comments-ce');
    this.add('ftcComments',                        'gtw-branch-and-withhold-comment-ce');
    this.add('addBranchTaxes',                     'gtw-branch-tax-creditable-add-ce');
    this.add('creditableBranchTaxEdit',            'gtw-branch-tax-creditable-edit-ce');
    this.add('addDefaultsBranchTaxEdit',           'gtw-branch-tax-defaults-edit-ce');
    this.add('branchTaxSummaryInfo',               'gtw-branchtax-summary-info-ce');
    this.add('withHoldTaxSummaryInfo',             'gtw-branchtax-summary-info-ce');
    this.add('ftcWithHoldTaxEdit',                 'gtw-withhold-tax-summary-edit-ce');
    this.add('editConfig',                         'gtw-config-861-ce');
    this.add('addConfig',                          'gtw-config-861-add-ce');
    this.add('htkoForm1118Config',                 'gtw-htko-config-ce');
    this.add('genBranchForm1118ConfigNew',         'gtw-htko-config-ce');
    this.add('editPercentage',                     'gtw-rd-sales-adj-percentage-edit-ce');
    this.add('addPercentage',                      'gtw-rd-sales-adj-percentage-add-ce');
    this.add('expConsolConf',                      'gtw-expense-consol-config-ce');
    this.add('editFTCSyncConfig',                  'gtw-k2-k3-sync-config-ce');
    this.add('systemSettings',                     'gtw-me-sic-assignment-ce');
    this.add('adjExpRnDSales',                     'gtw-rd-exp-contributed-ce');
    this.add('editAdjExpRndSales',                 'gtw-rd-exp-contributed-ce');
    this.add('adjIntexpSales',                     'gtw-int-exp-contributed-ce');
    this.add('editAdjIntexpSales',                 'gtw-int-exp-contributed-ce');
    this.add('adjRnDSales',                         'gtw-rd-sales-adjustment-ce');
    this.add('editRnDSales',                       'gtw-rd-sales-adjustment-ce');
    this.add('assetAdjustmentsFTC',                'gtw-sourcing-asset-adjustment-ce');
    this.add('assetEditAdjustmentsFTC',            'gtw-sourcing-asset-adjustment-ce');
    this.add('cfcInvestmentAdjEdit',               'gtw-cfc-investment-adj-edit-ce');
    this.add('cfcSplitOnOwnerPercentEdit',         'gtw-cfc-split-on-owner-percent-edit-ce');
    this.add('editPshipPtrBeatRatio',              'gtw-pship-ptr-ce');
    this.add('editPshipPtrRatio',                  'gtw-pship-ptr-ce');
    this.add('editSpecialAllocation',              'gtw-pship-ptr-ce');
    this.add('addBeatByConsol',                    'gtw-beat-type-consol-add-ce');
    this.add('transCategoryAdd',                   'gtw-expense-trans-add-edit-ce');
    this.add('transCategory',                      'gtw-expense-trans-add-edit-ce');
    this.add('accountUpdate',                      'gtw-expense-trans-account-details-ce');
    this.add('expenseTransInfo',                   'gtw-expense-trans-info-ce');
    this.add('attributeSetUpInfo',                 'gtw-attribute-set-up-info-ce');
    this.add('excludeAcct',                        'gtw-fir-fdii-qbai-exclude-acct-ce');
    this.add('includeAcct',                        'gtw-fir-fdii-qbai-exclude-acct-ce');
    this.add('k2k3otherpartsgriddelete',           'gtw-other-overrides-delete-ce');
    this.add('k2k3otherpartsbatchgriddelete',      'gtw-other-overrides-batch-delete-ce');
    this.add('trackScheduleL',                     'gtw-track-untrack-sch-l-ce');
    this.add('untrackScheduleL',                   'gtw-track-untrack-sch-l-ce');
    this.add('editAdjustGTAPSAttributes',          'gtw-fir-edit-gtaps-attributes-ce');
    this.add('addFTCDocument',                     'gtw-ftc-documentation-ce');
    this.add('investmentValidationEdit',           'gtw-investment-validation-ce');
    this.add('editAttachment',                     'gtw-add-attachments-ce');
    this.add('addAttachment',                      'gtw-add-attachments-ce');
    this.add('addComment',                         'gtw-add-comment-ce', { dialogWidth: '900px' });
    this.add('editComment',                        'gtw-add-comment-ce', { dialogWidth: '900px' });
    this.add('removeAttachment',                   'gtw-remove-attachments-ce');
    this.add('tbbsreclass',                        'gtw-tbbs-reclass-ce');
    this.add('editQar',                            'gtw-ftc-qar2018-ce');
    this.add('editAdjQar',                         'gtw-ftc-qar2018-ce');
    this.add('tagSummaryFtc',                      'gtw-ftc-sourcing-summary-ce');
    this.add('newTagSummaryFTC',                   'gtw-ftc-sourcing-summary-ce');
    this.add('attributeManagement',                'gtw-attribute-batch-ce');
    this.add('addNewTag',                          'gtw-add-new-tag-ce');
    this.add('lockComponent',                      'gtw-component-defaults-lock-unlock-ce');
    this.add('unlockComponent',                    'gtw-component-defaults-lock-unlock-ce');
    this.add('componentDefault',                   'gtw-component-defaults-edit-ce');
    this.add('editFTCAttribute',                   'gtw-ftc-attribute-edit-ce');
    this.add('lockAttributeManagement',            'gtw-ftc-attribute-lock-unlock-ce');
    this.add('unlockAttributeManagement',          'gtw-ftc-attribute-lock-unlock-ce');

    // ── foreign ──────────────────────────────────────────────────
    this.add('F8865-SCH-K1',                       'gtw-f8865-schd-k1-ce');
    this.add('Form8865-Schd-K1',                   'gtw-f8865-schd-k1-ce');
    this.add('Form5471-scheduleGInfo',             'gtw-form5471-schedule-g-ce');
    this.add('Form5471-scheduleIInfo',             'gtw-form5471-schedule-i-ce');
    this.add('Form5471-textInfo',                  'gtw-form5471-textinfo-ce');
    this.add('Form5471-scheduleG1Info',            'gtw-fir-5471-schedule-g1-ce');
    this.add('5471-ScheduleJ',                     'gtw-fir-5471-schedule-j-ce');
    this.add('5471-ScheduleF',                     'gtw-fir-5471-schedule-f-ce');
    this.add('5471-ScheduleO',                     'gtw-fir-5471-schedule-o-ce');
    this.add('5471-ScheduleI',                     'gtw-fir-5471-schedule-i-ce');
    this.add('5471-ScheduleM',                     'gtw-fir-5471-schedule-m-ce', { dialogHeight: '90vh' });
    this.add('conFinStmtCFC',                      'gtw-fir-f5471-h1-ce');
    this.add('conFinStmtPSHIP',                    'gtw-fir-f5471-h1-ce');
    this.add('conFinStmtDRE',                      'gtw-fir-f5471-h1-ce');
    this.add('conRtnCFC',                          'gtw-fir-f5471-h1-ce');
    this.add('conRtnPSHIP',                        'gtw-fir-f5471-h1-ce');
    this.add('conRtnDRE',                          'gtw-fir-f5471-h1-ce');
    this.add('othAdjCFC',                          'gtw-fir-f5471-h1-ce');
    this.add('othAdjPSHIP',                        'gtw-fir-f5471-h1-ce');
    this.add('othAdjDRE',                          'gtw-fir-f5471-h1-ce');
    this.add('8858-ScheduleM',                     'gtw-fir-8858-schedule-m-ce');
    this.add('8858-ScheduleG',                     'gtw-form-8858-textualinfo-ce');
    this.add('Form8858-textInfo',                  'gtw-form-8858-textualinfo-ce');
    this.add('8865-ScheduleO',                     'gtw-fir-8865-schedule-o-ce');
    this.add('8865-ScheduleP',                     'gtw-fir-8865-schedule-p-ce');
    this.add('Form8865-textInfo',                  'gtw-form8865-textual-ce');
    this.add('consolidated_gaap_amount',           'gtw-fir-consolidation-tb-ce');
    this.add('consolidated_ep_adj',                'gtw-fir-ep-drilldown-ce');
    this.add('firRepoint',                         'gtw-fir-repoint-ce');
    this.add('foreignLocalSysChartAcctMap',        'gtw-fir-local-sys-chart-acct-map-ce');
    this.add('foreignlocalSysChartAcctMapDesc',    'gtw-fir-local-acct-desc-ce');
    this.add('foreignSysFormChartAcctMap',         'gtw-fir-sys-form-chart-acct-map-ce');
    this.add('foreignLocalAcctTaxSens',            'gtw-fir-local-sys-chart-acct-map-ce');
    this.add('deleteQbuSourcingRecords',           'gtw-fir-qbu-sourcing-delete-record-ce');
    this.add('triggerGhostDefaultSourcing',        'gtw-fir-ghost-sourcing-ce');
    this.add('triggerGhostOwnershipProcess',       'gtw-fir-ghost-ownership-ce');
    this.add('triggerFIRFormRegeneration',         'gtw-fir-form-regen-ce');
    this.add('ghostcnonghostdelete',               'gtw-ghostc-nonghost-delete-ce');
    this.add('ghostepnonghostdelete',              'gtw-ghost-earnprofit-nonghost-delete-ce');
    this.add('ghostusdbnonghostdelete',            'gtw-ghost-usdbasispti-nonghost-delete-ce');
    this.add('ghostftcenonghostdelete',            'gtw-ghost-ftce-nonghost-delete-ce');
    this.add('ghostownershipinfodelete',           'gtw-ghost-ownership-info-delete-ce');
    this.add('settlementreclassesdelete',          'gtw-ghost-settlement-reclasses-delete-ce');
    this.add('open905cAnalysisBox',                'gtw-fir-trueup-taxes-ce');
    this.add('exisitng905Entity',                  'gtw-fir-trueup-taxes-ce');
    this.add('trueUpTaxesComments',                'gtw-trueup905c-comments-ce');
    this.add('analyzeComments',                    'gtw-fir-analyze-current-taxes-ce');
    this.add('epBumpIndUpdateYes',                 'gtw-fir-ep-bump-ce');
    this.add('epBumpIndUpdateNo',                  'gtw-fir-ep-bump-ce');
    this.add('sourcngInputScreenModal',            'gtw-sourcing-input-app-basket-ce');
    this.add('addNewSourcingBasket',               'gtw-sourcing-input-app-basket-ce');
    this.add('tagging163jModal',                   'gtw-fir-163j-tagging-ce');
    this.add('taggingsys163jModal',                'gtw-fir-163j-tagging-ce');
    this.add('trigger163japplydefaults',           'gtw-fir-163j-default-system-tagging-ce', { dialogWidth: '550px' });
    this.add('trigger163jresetdefaults',           'gtw-fir-163j-reset-default-tagging-ce', { dialogWidth: '550px' });
    this.add('sync163jto8990form',                 'gtw-fir-163j-sync-to-8990-ce', { dialogWidth: '550px' });
    this.add('trigger163jsyncprocess',             'gtw-fir-163j-sync-ce');
    this.add('delete163jOverrides',                'gtw-fir-overrides163j-ce', { dialogWidth: '550px' });
    this.add('trigger163jamtsync',                 'gtw-fir-163j-sync-ce');
    this.add('clearTransactions',                  'gtw-dcs-ghost-settlement-ce');
    this.add('processTransactions',                'gtw-dcs-ghost-settlement-ce');
    this.add('setCFCGroupEnable',                  'gtw-cfc-group-elect-ce');
    this.add('setCFCGroupDisable',                 'gtw-cfc-group-elect-ce');
    this.add('localSysAcctMap',                    'gtw-local-sys-chart-acct-map-ce');
    this.add('retainedEarningsExplanation',        'gtw-retained-earnings-ce');
    this.add('allLocalAdjustments',                'gtw-all-local-chart-adjustments-ce');
    this.add('project_tag',                        'gtw-project-tag-ce');
    this.add('project_tag_new',                    'gtw-project-tagging-ce');
    this.add('sourcing-project-tagging',            'gtw-sourcing-project-tagging-ce', { dialogHeight: '60vh' });
    this.add('domestic-project-tagging',            'gtw-domestic-project-tagging-ce', { dialogHeight: '60vh' });
    this.add('schedule-m-manual',                  'gtw-supplemental-info-ce');
    this.add('deleteChartAcctMap',                 'gtw-local-sys-chart-acct-map-delete-ce');
    this.add('updateDirectOwner',                  'gtw-non-financial-direct-owner-ce', { dialogHeight: '90vh' });
    this.add('updateTaxOwner',                     'gtw-non-financial-tax-owner-ce', { dialogHeight: '90vh' });
    this.add('deleteMaxLoanOverride',              'gtw-max-loan-delete-ce');
    this.add('consol_group_member_liab_edit',      'gtw-consol-group-edit-ce');
    this.add('ghostSettelmentRedirection',         'gtw-settlement-redirection-ce');
    this.add('ghostUpdateCombination',             'gtw-ghost-update-combination-ce');
    this.add('syncTestedUnits',                     'gtw-fir-tested-units-sync-ce', { dialogWidth: '350px' });
    this.add('addSchdmTemplateID',                 'gtw-add-schdm-template-id-ce');
    this.add('sourcing-default-me',                'gtw-sourcing-default-me-ce');
    this.add('defaultSourcingModal',               'gtw-batch-sourcing-default-me-ce');
    this.add('changeStatusTlc',                    'gtw-tax-liability-dashboard-ce');
    this.add('TaxLiabilityAmount',                 'gtw-tax-liability-dashboard-ce');

    // ── F1120 ────────────────────────────────────────────────────
    this.add('F1125-SCH-E',                        'gtw-f1125-sch-e-ce');
    this.add('F1120-SCH-K',                        'gtw-f1120-sch-k-ce');
    this.add('F1120-SCH-M3',                       'gtw-f1120-sch-m3-ce');
    this.add('F1120-SCH-B',                        'gtw-f1120-sch-b-ce');
    this.add('F1120-SCH-N',                        'gtw-f1120-sch-n-ce');
    this.add('F1120-8916',                         'gtw-f8916-ce');
    this.add('F1120SchdM2Modal',                   'gtw-f1120-sch-m2-retained-earnings-ce');
    this.add('scheduleJModal',                     'gtw-f1120-sch-j-ce');
    this.add('SubConsolOverride',                  'gtw-1120-subconsol-ce');
    this.add('F7004',                              'gtw-f7004-ce');
    this.add('F1125E',                             'gtw-f1125-sch-e-ce');
    this.add('F1120L-A-Modal',                     'gtw-f1120l-sch-a-ce');
    this.add('F1120L-B-Modal',                     'gtw-f1120l-sch-b-ce');
    this.add('F1120L-F-Modal',                     'gtw-f1120l-sch-f-ce');
    this.add('F1120L-G-Modal',                     'gtw-f1120l-sch-g-ce');
    this.add('F1120L-L-Modal',                     'gtw-f1120l-sch-l-ce');
    this.add('F1120L-M-Modal',                     'gtw-f1120l-sch-m-ce');
    this.add('F1120PC-C-Modal',                    'gtw-f1120pc-sch-c-ce');
    this.add('F1120PC-E-Modal',                    'gtw-f1120pc-sch-e-ce');
    this.add('F1120PC-F-Modal',                    'gtw-f1120pc-sch-f-ce');
    this.add('F1120PC-I-Modal',                    'gtw-f1120pc-sch-i-ce');
    this.add('F1120-Line22-Modal',                 'gtw-f1120-schc-line22-ce');
    this.add('SchdA163JDataRefresh',               'gtw-f163j-schd-a-data-refresh-ce', { dialogWidth: '550px' });
    this.add('EPPosting163J',                      'gtw-163j-auto-ep-ce', { dialogWidth: '550px' });
    this.add('EPPosting163JBatch',                 'gtw-163j-auto-ep-ce', { dialogWidth: '550px' });
    this.add('reclass_trigger',                    'gtw-163j-reclass-trigger-ce');
    this.add('allocRerun',                         'gtw-alloc-batch-rerun-ce');

    // ── Forms ────────────────────────────────────────────────────
    this.add('F1120-L-PC-Headers',                 'gtw-f1120-l-pc-headers-ce');
    this.add('f4626',                              'gtw-f4626-ce');
    this.add('8964ELE',                            'gtw-f8964-ele-ce', { dialogHeight: '90vh' });
    this.add('F4797GenericModal',                  'gtw-f-4797-ce');
    this.add('F4562GenericModal',                  'gtw-f-4562-ce', { dialogHeight: '90vh' });
    this.add('FSchddModal',                        'gtw-f-schdd-ce');
    this.add('F6252GenericModal',                  'gtw-f-6252-ce');
    this.add('F8824GenericModal',                  'gtw-f-8824-ce');
    this.add('F7205GenericModal',                  'gtw-f-7205-ce');
    this.add('ICDiagOutsideModal',                 'gtw-ic-diagnostics-ce', { dialogHeight: '90vh' });
    this.add('explainPayeevariance',               'gtw-related-party-modal-ce');

    // ── F965B ────────────────────────────────────────────────────
    this.add('F965-B',                             'gtw-f965b-ce');

    // ── FIR-8858 ─────────────────────────────────────────────────
    this.add('8858-Schedule-C1',                   'gtw-f8858-schd-c1-ce');

    // ── FIR-Form8865 ─────────────────────────────────────────────
    this.add('F8865-SCH-K',                        'gtw-f8865-schd-k-ce');
    this.add('Form8865-Schd-K',                    'gtw-f8865-schd-k-ce');

    // ── domestic ─────────────────────────────────────────────────
    this.add('Form4626Modal',                      'gtw-form4626-ce');
    this.add('FormScheduleDModal',                 'gtw-form-schedule-d-ce');
    this.add('Form4797Modal',                      'gtw-form4797-ce');
    this.add('Form4562Modal',                      'gtw-form4562-ce');
    this.add('form8082',                           'gtw-form-8082');
    this.add('F3380PartIIIModal',                  'gtw-credits-part-iii-ce');
    this.add('CounterPartyModalNew',               'gtw-counter-party-ce');
    this.add('CounterPartyModalA',                 'gtw-counter-party-ce');
    this.add('CounterPartyModalB',                 'gtw-counter-party-ce');
    this.add('CounterPartyModalC',                 'gtw-counter-party-ce');
    this.add('form8991Modal',                      'gtw-form8991-ce');
    this.add('scheduleDadj',                       'gtw-schedule-d-adj-ce');
    this.add('EST-Form-Modal',                     'gtw-est-form-ce');
    this.add('estimatePostingFromProvision',       'gtw-estimate-posting-ce');
    this.add('estimateMapping',                    'gtw-estimate-mapping-ce');
    this.add('section174modal',                    'gtw-section174-adj-ce');
    this.add('section174adjallocationsmodal',      'gtw-section174-adj-allocations-ce');
    this.add('851exclusionList',                   'gtw-exclusion-list-ce');
    this.add('assignPreparer',                     'gtw-assign-preparer-ce');
    this.add('qrevarianceExplain',                 'gtw-qre-variance-explain-ce');
    this.add('qreyoymodal',                        'gtw-qre-variance-yoy-ce');
    this.add('qreEntitytransformDetails',          'gtw-qre-entity-details-ce');
    this.add('qreEntitytransform',                 'gtw-qre-entity-transform-ce');
    this.add('qreEntityBusinesstransform',         'gtw-qre-entity-business-ce');
    this.add('ConsLedgers',                        'gtw-consolidation-ledgers-ce');
    this.add('creditDetailsModal',                 'gtw-credit-details-ce');
    this.add('settlementMainModal',                'gtw-settlement-main-ce');
    this.add('adjPriorYearData',                   'gtw-adj-prior-year-data-ce');
    this.add('paidDividendAdjs',                   'gtw-dividends-ce');
    this.add('receivedDividendAdjs',               'gtw-dividends-ce');
    this.add('dividendAdjs',                       'gtw-dividends-ce');
    this.add('newDividendAdd',                     'gtw-dividends-ce');
    this.add('goldAddressChange',                  'gtw-gold-address-ce');
    this.add('localAcctDesc',                      'gtw-gl-local-acct-desc-ce');
    this.add('trailBalAmount',                     'gtw-trial-balance-ce');
    this.add('methodSettlement',                    'gtw-method-settlement-ce');
    this.add('gofSettlement',                      'gtw-gof-settlement-ce');
    this.add('auditCells',                         'gtw-grid-audit-cells-ce');
    this.add('gridActionModal',                    'gtw-grid-action-modal-ce', { dialogWidth: '70vw' });
    this.add('gridPivotActionModal',               'gtw-grid-action-modal-ce', { dialogWidth: '70vw' });
    this.add('mappingwwni',                        'gtw-wwni-mapping-ce');
    this.add('deleteChartcode',                    'gtw-local-chart-delete-ce');
    this.add('deleteDdData',                       'gtw-pdf-data-delete-ce');
    this.add('deleteFbData',                       'gtw-pdf-data-delete-ce');
    this.add('deleteRpData',                       'gtw-pdf-data-delete-ce');
    this.add('deleteS3Data',                       'gtw-pdf-data-delete-ce');
    this.add('updateEntityDetails',                'gtw-entity-details-ce');
    this.add('excludedpopup',                      'gtw-review-adj-automation-ce');
    this.add('excludedpopupcurrentyear',           'gtw-review-adj-automation-ce');
    this.add('includepopup',                       'gtw-review-adj-automation-ce');
    this.add('recommeauto',                        'gtw-recommended-automations-ce');
    this.add('recommeautopopup',                   'gtw-recommended-automations-confirm-ce');
    this.add('taxAttribModal',                     'gtw-tax-attributes-ce');
    this.add('taxAttribEntityModal',               'gtw-tax-attributes-ce');
    this.add('dashAttachment901m',                 'gtw-dashboard-901m-ce');
    this.add('adjComments901m',                    'gtw-dashboard-901m-ce');
    this.add('reasonFor901m',                      'gtw-dashboard-901m-ce');
    this.add('adjustmentsFor901m',                 'gtw-dashboard-901m-ce');
    this.add('post_901_adjustments',               'gtw-dashboard-901m-ce');
    this.add('insert_category_data',               'gtw-related-party-insert-category-ce');
    this.add('change_category_data',               'gtw-related-party-change-system-acct-ce');
    this.add('RedirectLeidLink',                   'gtw-summary-details-link-ce');
    this.add('schP_save',                          'gtw-sch-p-save-ce');

    // ── review / submission dashboard ─────────────────────────────
    this.add('addDormat',                          'gtw-add-dormat-ce', { dialogWidth: '600px' });
    this.add('removeDormat',                       'gtw-remove-dormat-ce', { dialogWidth: '420px' });

    // ── admin ────────────────────────────────────────────────────
    this.add('get_scenario_setup',                 'gtw-admin-scenario-setup-ce');
    this.add('editSelectedValidationRule',         'gtw-admin-validation-rule-edit-ce');
    this.add('rulesEngineFn',                      'gtw-admin-rules-engine-supp-details-ce');
    this.add('entitychartcleanup',                 'gtw-entity-chart-map-clean-up');
    this.add('uploadFileToS3',                     'gtw-s3-file-upload-ce', { dialogWidth: '92vw', dialogHeight: '58vh' });
    this.add('uploadPreformattedDisclousure',      'gtw-s3-file-upload-ce', { dialogWidth: '92vw', dialogHeight: '58vh' });
    this.add('Reset Datasource Connection',        'gtw-admin-reset-ds-connection-ce');
    this.add('postingPartners',                    'gtw-posting-partners-ce', { dialogWidth: '1200px' });
    this.add('createNewChartRollover',             'gtw-chart-rollover-maintenance-ce', { dialogWidth: '800px' });
    this.add('createAmendedCycle',                 'gtw-amended-cycle-ce');
    this.add('dfMapping',                          'gtw-df-mapping-changes-ce', { dialogWidth: '840px' });
    this.add('maintNotif',                         'gtw-notif-engine-ce');
    this.add('secondaryActionDashboard',           'gtw-secondary-action-dashboard-ce');
    this.add('Request-Params-Modal',               'gtw-batch-monitoring-ce');
    this.add('taxSensitiveAcct',                   'gtw-tax-sensitive-acct-ce');
    this.add('entitycodecombination',              'gtw-entity-chart-map-ce', { templateMode: 'chartmap' });
    this.add('All_Entity_Details_Modal',           'gtw-entity-chart-map-ce', { templateMode: 'entity-edit' });
    this.add('entitychartupdate',                  'gtw-entity-chart-map-ce', { templateMode: 'update' });
    this.add('delete_tbl_data',                    'gtw-tbl-maintenance-ce');
    this.add('insert_tbl_data',                    'gtw-tbl-maintenance-ce', { templateMode: 'insert' });
    this.add('updateDocumentDetail',               'gtw-update-document-detail-ce');
    this.add('cosmeticEntityUpdate',               'gtw-entity-update-ce');
    this.add('updateChangeList',                   'gtw-update-change-list-ce');
    this.add('mefEfileChanges',                    'gtw-mef-efile-changes');
    this.add('batch-Request-Modal',                'gtw-batch-request-ce');
    this.add('basketPostModal',                    'gtw-basket-post-ce');
    this.add('basketPostdelete',                   'gtw-basket-post-delete-ce');
    this.add('maintainanceOwnerModal',             'gtw-maintenance-owner-ce');
    this.add('maintainanceOwnerdelete',            'gtw-maintenance-owner-delete-ce');
    this.add('createConsolGroup',                  'gtw-consol-group-ce');
    this.add('aggRerun',                           'gtw-aggregration-rerun-ce');
    this.add('consolcalcSchJRerun',                'gtw-aggregration-rerun-ce');
    this.add('consolcalcCreditsRerun',             'gtw-aggregration-rerun-ce');
    this.add('aggSecondaryAction',                 'gtw-aggregration-rerun-ce');
    this.add('delta_posting',                      'gtw-delta-posting-ce');
    this.add('add_code',                            'gtw-naics-maintenance', { dialogWidth: '70vw' });

    // ── efile ────────────────────────────────────────────────────
    this.add('efileAddEditIrsSubmission',          'gtw-efile-irs-submission-ce');
    this.add('efileProperties',                    'gtw-efile-properties-ce');
    this.add('efileSoftwareAddEditModal',          'gtw-efile-software-data-create-edit-ce');
    this.add('efileDiagnosticsConfigPropertiesModal', 'gtw-efile-add-edit-diagnostics-ce');
    this.add('efileAddEditReadOnly',               'gtw-efile-add-edit-object-ce');
    this.add('efileAddtionalElements',             'gtw-efile-additional-elements-ce');
    this.add('efileDataDefinition',                'gtw-efile-data-definition-ce');
    this.add('efileSchemaSet',                     'gtw-efile-schema-set-ce');
    this.add('efileConfigPropertiesModal',         'gtw-efile-tmef-config-ce');
    this.add('efileEntityMapModal',                'gtw-efile-tmef-entity-map-ce');
    this.add('efileAddEditKeyModel',               'gtw-efile-edit-key-table-data-ce');
    this.add('efileAddEditSubMnfstModel',          'gtw-efile-submission-manifest-table-ce');
    this.add('efileAddEditSubmsnModel',            'gtw-efile-edit-submission-table-data-ce');
    this.add('pingToIRS',                          'gtw-efile-ping-irs-ce', { dialogWidth: '420px' });
    this.add('uploadIRSSchema',                    'gtw-efile-upload-irs-schema');
    this.add('uploadFilesToEfileDirectory',        'gtw-efile-upload-files-ce');
    this.add('xml2ElementsCompare',                'gtw-xml-vs-elements-ce');
    // DataFilters screen 11618 "Extract Elements from XML" — legacy
    // DataFiltersController.js L263-L266 opened the same
    // `efileAdmin/templates/xml-vs-elements.html` / `XmlVsElementsController`
    // as the grid `xml2ElementsCompare` callback, with `size: 'sm'`
    // (350px, per the shared modal_size mapping).
    this.add('extractElementsFromXml',             'gtw-xml-vs-elements-ce', { dialogWidth: '350px' });
    this.add('syncAllXMLsToSLT',                   'gtw-bulk-xml-sync-ce');
    this.add('Process 5713 Lines',                 'gtw-process-5713-lines-ce');
    this.add('viewEntitiesSelections',             'gtw-view-bulk-export-selection-ce', {
      dialogWidth: '92vw',
      dialogHeight: '90vh',
    });
    this.add('viewFormsSelections',                'gtw-view-bulk-export-selection-ce', {
      dialogWidth: '92vw',
      dialogHeight: '90vh',
    });
    this.add('s3TemplateView',                     'gtw-s3-template-view-ce', { dialogWidth: '92vw', dialogHeight: '90vh' });
    // Upload template: compact dialog (expected UI); height follows content — no fixed 90vh shell.
    this.add('s3TemplateView-sm',                  'gtw-s3-template-view-ce', {
      dialogWidth: '560px',
      dialogPanelClass: 'ce-host-dialog-panel ce-host-dialog-s3-upload',
    });

    // ── entity-classification ────────────────────────────────────
    this.add('createEntityClassification',         'gtw-entity-classification-create-ce');
    // DataFilters screen 11149 — same CE as legacy entityClassificationCreateActionController
    this.add('createNewOverrideEntity',            'gtw-entity-classification-create-ce');
    this.add('updateEntityClassification',         'gtw-entity-classification-update-ce');
    this.add('reviewEntities',                     'gtw-sync-entity-classification-ce');
    this.add('saveEntities',                       'gtw-sync-entity-classification-ce');
    this.add('syncEntitesToGTW',                   'gtw-sync-entity-classification-ce');
    this.add('acceptSave',                         'gtw-sync-entity-classification-ce');
    this.add('updateReferenceTableEntityClassification', 'gtw-reference-table-ce');
    this.add('updateConfigurationEntityClassification', 'gtw-ec-configuration-ce');
    this.add('createConfigurationEntityClassification', 'gtw-ec-configuration-ce');
    this.add('updateEcRefreshSetup',               'gtw-ec-refresh-setup-ce');
    this.add('createScenarioSetup',                'gtw-scenario-setup-ce');
    this.add('updateScenarioSetup',                'gtw-scenario-setup-ce');
    this.add('createEntityFiling',                 'gtw-entity-filling-ce');
    this.add('editEntityFilling',                  'gtw-entity-filling-ce');
    this.add('updateFilingDatesEntity',            'gtw-entity-filling-ce');
    this.add('updateQuarterMapEntity',             'gtw-ec-quartermap-ce');
    this.add('updateECThresholdMgmt',              'gtw-ec-threshold-setup-ce');
    // DataFilters screen 11468 "Add New" — reuse same CE as update (create mode = empty rowData)
    this.add('createThresholdManagement',          'gtw-ec-threshold-setup-ce');
    this.add('editConfigData',                     'gtw-config851-data-ce');
    this.add('create851ConfigEntity',              'gtw-config851-data-ce');    // screen 11555

    // DataFilters "Create …" pencil-click modals (see DataFiltersController.js
    // L206-L220). Each screen maps to the same CE used by its update/edit
    // counterpart above so the create flow reuses the migrated component.
    this.add('createScenarioEntity',               'gtw-scenario-setup-ce');    // screen 11163
    this.add('createReferenceTableEntity',         'gtw-reference-table-ce');   // screen 11165
    this.add('createConfigurationEntity',          'gtw-ec-configuration-ce');  // screen 11166
    this.add('createFilingDateEntity',             'gtw-entity-filling-ce');    // screen 11167
    this.add('createRefreshSetupEntity',           'gtw-ec-refresh-setup-ce');  // screen 11446
    this.add('createQuarterMapEntity',             'gtw-ec-quartermap-ce');     // screen 11451

    // ── cbc ──────────────────────────────────────────────────────
    this.add('bepsTaxesPaid',                      'gtw-cbc-reporting-ce');
    this.add('editTbl2Data',                       'gtw-cbc-beps-countries-ce');
    this.add('cbcDeleteVarianceDataModal',         'gtw-cbc-delete-variance-data-ce');
    this.add('cbcAddFsElimDataModal',              'gtw-cbc-create-fs-elim-data-ce');
    this.add('cbcAddRecDataModal',                 'gtw-cbc-create-rec-data-ce');
    this.add('cbcTbl3Modal',                       'gtw-cbc-schd-a-tbl3-ce');
    this.add('tbl2Refresh',                        'gtw-cbc-tbl2-refresh-ce');
    this.add('cbyeRefresh',                        'gtw-cbc-cbye-refresh-ce');
    this.add('cbcacctagging',                      'gtw-cbc-edit-acct-tagging-ce');
    this.add('deleteCbcData',                      'gtw-cbc-delete-data-by-me-ce');
    this.add('statelessRefresh',                   'gtw-cbc-stateless-refresh-ce');
    this.add('statelessPush',                      'gtw-cbc-stateless-push-ce');
    this.add('addBepsAdminQauters',                'gtw-cbc-beps-quarters-ce');
    this.add('updateBepsAdminQuarter',             'gtw-cbc-beps-quarters-ce');
    this.add('addBepsAdminCategory',               'gtw-cbc-beps-categories-ce');
    this.add('deleteBepsCategory',                 'gtw-cbc-beps-categories-ce');
    this.add('bepsRollforward',                    'gtw-beps-rollforward-ce');
    this.add('pushCBCdataToEfile',                'gtw-push-cbc-data-to-efile-ce');

    // ── DST ──────────────────────────────────────────────────────
    this.add('dstRollOver',                        'gtw-dst-rollover-modal-ce');
    this.add('dstFormModal',                       'gtw-dst-form-create-edit-ce', { dialogWidth: '1000px' });
    this.add('deleteBasisData',                    'gtw-dst-admin-delete-modal-ce');
    this.add('deleteCurrData',                     'gtw-dst-admin-delete-modal-ce');
    this.add('deleteStkTypeData',                  'gtw-dst-admin-delete-modal-ce');
    this.add('deleteRetPeriodData',                'gtw-dst-admin-delete-modal-ce');
    this.add('deleteStirData',                     'gtw-dst-admin-delete-modal-ce');
    this.add('push-dst-pdf-efile',                 'gtw-dst-efile-pdf-push-ce');
    this.add('delete-dst-pdf',                     'gtw-dst-efile-pdf-push-ce');
    this.add('efile-data-push',                    'gtw-dst-efile-datapush-ce');
    this.add('viewDSTPdf',                         'gtw-dst-view-pdf-of-efile-ce', { dialogWidth: '480px' });

    // ── slt-dashboard ────────────────────────────────────────────
    this.add('slt-api-response-modal',             'gtw-slt-api-response-ce');
    this.add('slt-sync-error-modal',               'gtw-slt-sync-error-ce');
    this.add('slt-is-duplicate-modal',             'gtw-slt-is-duplicate-ce');
    this.add('slt-key-setup-modal',                'gtw-slt-key-setup-ce');
    this.add('slt-data-generation-modal',          'gtw-slt-data-generation-ce');
    this.add('Retrigger Group Sync',               'gtw-retrigger-group-sync-ce');
    this.add('Change-Log-Monitor-Modal',           'gtw-change-log-monitor-ce');
    this.add('Retrigger SNS Message',              'gtw-retrigger-message-ce');
    this.add('Run Regeneration Process',           'gtw-regeneration-process-ce');
    this.add('Re-run Pdf generation',              'gtw-rerun-pdf-generation-ce');
    this.add('Re-run XML generation',              'gtw-rerun-xml-generation-ce');

    // ── slt-api-dashboard ────────────────────────────────────────
    this.add('editServiceModal',                   'gtw-slt-add-data-service-ce');
    this.add('createDataServiceModal',             'gtw-slt-add-data-service-ce');
    this.add('slt-data-service-request-modal',     'gtw-slt-data-service-request-ce');
    this.add('slt-api-test-modal',                 'gtw-slt-data-service-ce');

    // ── state-entity-groups ──────────────────────────────────────
    this.add('stateEditGroup',                     'gtw-state-group-edit-ce');
    this.add('assignEntitiesToStateGroups',        'gtw-state-group-assign-ce');
    this.add('stateDeleteGroups',                  'gtw-state-group-delete-ce');
    this.add('stateCreateGroups',                  'gtw-state-group-add-ce');
    this.add('editExternalEntity',                 'gtw-external-state-entity-edit-ce');
    this.add('deleteExternalEntity',               'gtw-external-state-entity-delete-ce');
    this.add('createExternalEntity',               'gtw-external-state-entity-edit-ce');

    // ── section965 ───────────────────────────────────────────────
    this.add('section965FilingGroup',              'gtw-section-965-fg-ce');
    this.add('section965NovDecAdjs',               'gtw-section-965-nov-dec-ce');
    this.add('section9651231Adjs',                 'gtw-section-965-12-31-ce');
    this.add('section965Adjustments',              'gtw-section-965-adjs-ce');

    // ── related-party-payment ────────────────────────────────────
    this.add('relatedPartyPaymentModal',           'gtw-related-party-payment-type-ce');
    this.add('relatedpartypaymentsmodal1',         'gtw-related-party-payment-add-ce');
    this.add('relatedpartypaymentdelete',          'gtw-related-party-payment-delete-ce');
    this.add('m3_allocate_payor',                  'gtw-m3-payor-allocate-ce');

    // ── recurring-ep ─────────────────────────────────────────────
    this.add('recurringMultiYearTaxAdjustment',    'gtw-recurring-multi-year-tax-adjustment-ce');
    this.add('openRecurringEPDialog',              'gtw-recurring-ep-ce');

    // ── newProforma ──────────────────────────────────────────────
    this.add('retryFailedEntities',                'gtw-retry-failed-entities-ce');
    this.add('downloadFile',                       'gtw-pdf-bulk-download-ce', {
      dialogWidth: '440px',
      // Shrink-wrap CE height; default CE host uses flex:1 which stretches tiny modals.
      dialogPanelClass: 'ce-host-dialog-panel ce-host-dialog-pdf-bulk-download',
    });

    // ── wwni-sub-category ────────────────────────────────────────
    this.add('wwnisubCateBusi',                    'gtw-wwni-sub-category-ce');
    this.add('wwnisubCateEntity',                  'gtw-wwni-sub-category-ce');

    // ── remaining entries (aliased imports resolved manually) ─────
    this.add('F1125-SCH-A',                        'gtw-f1125-sch-a-ce');
    this.add('F1125A',                             'gtw-f1125-sch-a-ce');
    this.add('F4626GenericModal',                  'gtw-f-4626-ce');
    this.add('GenericFormModal',                   'gtw-genericforms-ce');
    this.add('F1120SchdM3Modal',                   'gtw-form-schedule-m3-ce');
    this.add('open5471Pdf',                        'gtw-tb-pdf-preview-ce');
    this.add('deleteThresholdMgmtData',            'gtw-ec-threshold-delete-ce');
    this.add('deleteCreditFacility',               'gtw-delete-credit-facility');
    this.add('getConsolePDF',                      'gtw-get-console-pdf');
    this.add('getEntityPDF',                       'gtw-get-entity-pdf');
    this.add('batch_allocate_payor',               'gtw-related-party-payment-allocation-ce');
    this.add('triggerAggregation',                 'gtw-aggre-dashboard-ce');
    this.add('reviewAutoCheckbox',                 'gtw-aggre-dashboard-ce');

    // ── book-to-tax summary-by-acct modals ─────────────────────
    this.add('reclassBookByLocalChart',            'gtw-reclass-local-chart-ce');
    this.add('epTaxAdjsLocalChart',                'gtw-ep-adjs-local-chart-ce');
    this.add('epTaxAdjsLocalCharttest',            'gtw-ep-adjs-local-chart-ce');
    this.add('elimReclassAdjsByLocalChart',         'gtw-elim-reclass-local-ce');

    // ── foreign admin sync modals (FX amount columns / FX rates / Spot rates) ──
    this.add('triggerFxAmtColumnSync',             'gtw-fx_amt-sync-ce',             { dialogWidth: '420px' });
    this.add('triggerFxAmtColumnBatchSync',        'gtw-fx_amt-sync-ce',             { dialogWidth: '420px' });
    this.add('triggerFxRatesSync',                 'gtw-fx_rates-sync-ce',           { dialogWidth: '420px' });
    this.add('triggerFxRatesBatchSync',            'gtw-fx_rates-sync-ce',           { dialogWidth: '420px' });
    this.add('triggerSpotRatesAllCurrSync',        'gtw-spotrates-all-curr-sync-ce', { dialogWidth: '420px' });
    this.add('syncForeignAllCurr',                 'gtw-spotrates-all-curr-sync-ce', { dialogWidth: '420px' });
  }

  add(callBack: string, ceTagName: string, opts?: Partial<ModalRegistryEntry>): void {
    this.registry.set(callBack, {
      ceTagName,
      dialogWidth: opts?.dialogWidth ?? '90vw',
      dialogHeight: opts?.dialogHeight,
      dialogPanelClass: opts?.dialogPanelClass ?? 'ce-host-dialog-panel',
      templateMode: opts?.templateMode,
    });
  }

  resolve(callBack: string): ModalRegistryEntry | undefined {
    const explicit = this.registry.get(callBack);
    if (explicit) {
      return explicit;
    }
    if (callBack && ALL_CUSTOM_ELEMENT_TAGS.has(callBack)) {
      return {
        ceTagName: callBack,
        dialogWidth: '90vw',
        dialogPanelClass: 'ce-host-dialog-panel',
      };
    }
    return undefined;
  }

  has(callBack: string): boolean {
    return this.registry.has(callBack) || ALL_CUSTOM_ELEMENT_TAGS.has(callBack);
  }
}
